/**
 * Système de monitoring des requêtes Supabase
 * Permet de tracer et analyser toutes les requêtes pour détecter les problèmes de performance
 */

export type QueryLog = {
  id: number;
  timestamp: string;
  table: string;
  operation: 'select' | 'insert' | 'update' | 'delete' | 'upsert';
  filters?: string;
  duration: number;
  rowCount?: number;
  source?: string; // Fonction/composant qui a déclenché la requête
};

class SupabaseMonitor {
  private logs: QueryLog[] = [];
  private logId = 0;
  private enabled = false;
  private startTime: number = Date.now();

  enable() {
    this.enabled = true;
    this.startTime = Date.now();
    this.logs = [];
    this.logId = 0;
    // eslint-disable-next-line no-console
    console.log('🔍 Supabase Monitor activé - Tracking des requêtes démarré');
  }

  disable() {
    this.enabled = false;
    // eslint-disable-next-line no-console
    console.log('🔍 Supabase Monitor désactivé');
  }

  isEnabled() {
    return this.enabled;
  }

  log(query: Omit<QueryLog, 'id' | 'timestamp'>) {
    if (!this.enabled) return;

    const log: QueryLog = {
      id: ++this.logId,
      timestamp: new Date().toISOString(),
      ...query,
    };

    this.logs.push(log);

    // Console log formaté
    const emoji = this.getOperationEmoji(log.operation);
    // eslint-disable-next-line no-console
    console.log(
      `${emoji} Query #${log.id} [${log.duration}ms]`,
      `${log.table}.${log.operation}`,
      log.filters ? `(${log.filters})` : '',
      log.rowCount !== undefined ? `→ ${log.rowCount} rows` : ''
    );
  }

  private getOperationEmoji(operation: QueryLog['operation']): string {
    switch (operation) {
      case 'select':
        return '📖';
      case 'insert':
        return '➕';
      case 'update':
        return '✏️';
      case 'delete':
        return '🗑️';
      case 'upsert':
        return '🔄';
      default:
        return '📊';
    }
  }

  getLogs() {
    return this.logs;
  }

  getSummary() {
    const byTable = this.logs.reduce(
      (acc, log) => {
        acc[log.table] = (acc[log.table] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    const byOperation = this.logs.reduce(
      (acc, log) => {
        acc[log.operation] = (acc[log.operation] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    const totalDuration = this.logs.reduce((sum, log) => sum + log.duration, 0);
    const avgDuration = this.logs.length > 0 ? Math.round(totalDuration / this.logs.length) : 0;

    return {
      total: this.logs.length,
      byTable,
      byOperation,
      totalDuration,
      avgDuration,
      sessionDuration: Date.now() - this.startTime,
    };
  }

  /**
   * Exporte les logs en JSON stringifié pour copier/coller
   */
  exportLogsAsJSON(): string {
    const summary = this.getSummary();
    const export_data = {
      metadata: {
        exportedAt: new Date().toISOString(),
        sessionDuration: `${Math.round(summary.sessionDuration / 1000)}s`,
        totalQueries: summary.total,
        totalDuration: `${summary.totalDuration}ms`,
        avgDuration: `${summary.avgDuration}ms`,
      },
      summary: {
        byTable: summary.byTable,
        byOperation: summary.byOperation,
      },
      queries: this.logs.map((log) => ({
        id: log.id,
        time: log.timestamp,
        query: `${log.table}.${log.operation}`,
        filters: log.filters,
        duration: `${log.duration}ms`,
        rows: log.rowCount,
        source: log.source,
      })),
    };

    return JSON.stringify(export_data, null, 2);
  }

  /**
   * Exporte un rapport texte lisible
   */
  exportLogsAsText(): string {
    const summary = this.getSummary();
    let text = '═══════════════════════════════════════\n';
    text += '📊 SUPABASE QUERY MONITOR REPORT\n';
    text += '═══════════════════════════════════════\n\n';

    text += `Session Duration: ${Math.round(summary.sessionDuration / 1000)}s\n`;
    text += `Total Queries: ${summary.total}\n`;
    text += `Total Duration: ${summary.totalDuration}ms\n`;
    text += `Average Duration: ${summary.avgDuration}ms\n\n`;

    text += '─── BY TABLE ───\n';
    Object.entries(summary.byTable)
      .sort(([, a], [, b]) => (b as number) - (a as number))
      .forEach(([table, count]) => {
        text += `  ${table}: ${count} queries\n`;
      });

    text += '\n─── BY OPERATION ───\n';
    Object.entries(summary.byOperation).forEach(([op, count]) => {
      text += `  ${op}: ${count} queries\n`;
    });

    text += '\n─── DETAILED QUERIES ───\n';
    this.logs.forEach((log) => {
      text += `\n#${log.id} [${log.duration}ms] ${log.table}.${log.operation}`;
      if (log.filters) text += ` (${log.filters})`;
      if (log.rowCount !== undefined) text += ` → ${log.rowCount} rows`;
      if (log.source) text += `\n    Source: ${log.source}`;
      text += '\n';
    });

    text += '\n═══════════════════════════════════════\n';
    return text;
  }

  clear() {
    this.logs = [];
    this.logId = 0;
    this.startTime = Date.now();
    // eslint-disable-next-line no-console
    console.log('🧹 Query logs cleared');
  }
}

export const supabaseMonitor = new SupabaseMonitor();

/**
 * Helper pour wrapper une requête Supabase et la monitorer
 * Accepte un QueryBuilder Supabase (PromiseLike) ou une Promise
 */
export async function monitorQuery<T>(
  table: string,
  operation: QueryLog['operation'],
  filters: string | undefined,
  queryFn: () => any, // Accepte QueryBuilder ou Promise de Supabase
  source?: string
): Promise<{ data: T | null; error: any }> {
  const startTime = Date.now();

  // Exécuter et attendre le résultat (fonctionne avec Promise et PromiseLike)
  const result = (await queryFn()) as { data: T | null; error: any };
  const duration = Date.now() - startTime;

  let rowCount: number | undefined;
  if (result.data) {
    rowCount = Array.isArray(result.data) ? result.data.length : 1;
  }

  supabaseMonitor.log({
    table,
    operation,
    filters,
    duration,
    rowCount,
    source,
  });

  return result;
}
